import type {
  Paginated,
  QuotationDetails,
  QuotationStatus,
  QuotationSummary,
  SalesOrderDetails,
  SalesOrderStatus,
  SalesOrderSummary,
} from '@ldembalagens/types';
import { api, buildUrl, openFile, type QueryParams } from '@/lib/api-client';

export interface DocumentItemInput {
  id?: string;
  productId: string;
  specifications?: string | null;
  quantity: string;
  /** Omitido em produtos vendidos por m³ (a API calcula pelo volume) */
  unitPrice?: string;
  discount?: string;
  lengthMm?: string | null;
  widthMm?: string | null;
  thicknessMm?: string | null;
  pricePerM3?: string | null;
}

export interface QuotationInput {
  customerId: string;
  issueDate?: string;
  validUntil?: string | null;
  productionLeadDays?: number | null;
  discount?: string;
  freight?: string;
  taxes?: string;
  paymentTerms?: string | null;
  deliveryAddress?: string | null;
  notes?: string | null;
  internalNotes?: string | null;
  items: DocumentItemInput[];
}

export interface SalesOrderInput {
  customerId: string;
  orderDate?: string;
  deliveryDate?: string | null;
  discount?: string;
  freight?: string;
  taxes?: string;
  paymentTerms?: string | null;
  deliveryAddress?: string | null;
  notes?: string | null;
  items: DocumentItemInput[];
}

export interface SalesQuery extends QueryParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: QuotationStatus | SalesOrderStatus;
  customerId?: string;
  from?: string;
  to?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export const quotationsApi = {
  list: (query: SalesQuery) => api.get<Paginated<QuotationSummary>>('/sales/quotations', query),
  get: (id: string) => api.get<QuotationDetails>(`/sales/quotations/${id}`),
  create: (input: QuotationInput) => api.post<QuotationDetails>('/sales/quotations', input),
  update: (id: string, input: Partial<QuotationInput>) =>
    api.patch<QuotationDetails>(`/sales/quotations/${id}`, input),
  status: (id: string, status: QuotationStatus, reason?: string) =>
    api.post<QuotationDetails>(`/sales/quotations/${id}/status`, { status, reason }),
  convert: (id: string, input: { deliveryDate?: string; confirm?: boolean }) =>
    api.post<{ quotation: QuotationDetails; orderId: string }>(
      `/sales/quotations/${id}/convert`,
      input,
    ),
  remove: (id: string) => api.delete(`/sales/quotations/${id}`),
  pdfUrl: (id: string) => buildUrl(`/sales/quotations/${id}/pdf`),
};

export const salesOrdersApi = {
  list: (query: SalesQuery) => api.get<Paginated<SalesOrderSummary>>('/sales/orders', query),
  get: (id: string) => api.get<SalesOrderDetails>(`/sales/orders/${id}`),
  create: (input: SalesOrderInput) => api.post<SalesOrderDetails>('/sales/orders', input),
  update: (id: string, input: Partial<SalesOrderInput>) =>
    api.patch<SalesOrderDetails>(`/sales/orders/${id}`, input),
  status: (id: string, status: SalesOrderStatus, reason?: string) =>
    api.post<SalesOrderDetails>(`/sales/orders/${id}/status`, { status, reason }),
  remove: (id: string) => api.delete(`/sales/orders/${id}`),
  /** Abre o PDF do pedido (timbre da empresa) numa nova aba. */
  openPdf: (id: string) => openFile(`/sales/orders/${id}/pdf`),
};

export const salesKeys = {
  quotations: ['sales', 'quotations'] as const,
  quotationList: (q: SalesQuery) => ['sales', 'quotations', 'list', q] as const,
  quotation: (id: string) => ['sales', 'quotations', 'detail', id] as const,
  orders: ['sales', 'orders'] as const,
  orderList: (q: SalesQuery) => ['sales', 'orders', 'list', q] as const,
  order: (id: string) => ['sales', 'orders', 'detail', id] as const,
  all: ['sales'] as const,
};
